import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  LayoutDashboard,
  Home,
  Heart,
  MessageSquare,
  Settings,
  LogOut,
  Building2,
  Users,
  Calendar,
  BarChart3,
  Calculator,
  Map as MapIcon,
  Scale,
  FileUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { NotificationBell } from "@/components/NotificationBell";
import { cn } from "@/lib/utils";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", href: "/dashboard" },
    { icon: Building2, label: "Mis Propiedades", href: "/dashboard/properties" },
    { icon: FileUp, label: "Carga Masiva", href: "/dashboard/admin/bulk-upload" },
    { icon: Heart, label: "Favoritos", href: "/dashboard/favorites" },
    { icon: MessageSquare, label: "Mensajes", href: "/dashboard/messages" },
    { type: 'separator', label: 'CRM CORRETAJE' },
    { icon: Users, label: "Clientes", href: "/dashboard/crm/clients" },
    { icon: Calendar, label: "Visitas", href: "/dashboard/crm/visits" },
    { icon: BarChart3, label: "Pipeline", href: "/dashboard/crm/pipeline" },
    { icon: BarChart3, label: "Informes", href: "/dashboard/crm/reports" },
    { type: 'separator', label: 'HERRAMIENTAS' },
    { icon: Calculator, label: "Tasaciones", href: "/dashboard/valuations" },
    { icon: MapIcon, label: "Mapa Inmobiliario", href: "/dashboard/map" },
    { icon: Scale, label: "Soporte Legal/Contable", href: "/dashboard/support" },
    ...(user?.role === 'admin' ? [
      { type: 'separator', label: 'ADMINISTRACIÓN' },
      { icon: Building2, label: "Todas las Propiedades", href: "/dashboard/admin/properties" },
      { icon: Users, label: "Todos los Usuarios", href: "/dashboard/admin/users" }
    ] : []),
    { type: 'separator' },
    { icon: Settings, label: "Configuración", href: "/dashboard/settings" }
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!user) {
    return null;
  }

  return (
    <div className="flex h-screen bg-parchment pt-20">
      {/* Sidebar */}
      <aside className="w-72 bg-parchment border-r-2 border-ink fixed h-[calc(100vh-5rem)] z-30">
        <div className="h-full flex flex-col">
          {/* User Info Editorial */}
          <div className="p-6 border-b-2 border-ink">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-ink flex items-center justify-center text-parchment brutalist-border-sm">
                <span className="text-xl font-display font-black">
                  {user.name[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-black uppercase text-sm truncate">{user.name}</h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-terracotta truncate">{user.role || 'BROKER'}</p>
              </div>
              <NotificationBell />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto scrollbar-hide">
            <ul className="space-y-1">
              {menuItems.map((item, idx) => {
                if (item.type === 'separator') {
                  return (
                    <li key={`sep-${idx}`} className="pt-6 pb-2">
                      <span className="px-4 text-[9px] font-black uppercase tracking-[0.3em] text-ink/30">{item.label}</span>
                    </li>
                  );
                }
                const Icon = item.icon;
                const isActive = item.href ? location.pathname === item.href : false;
                return (
                  <li key={item.href || `item-${idx}`}>
                    <button
                      className={cn(
                        "w-full flex items-center px-4 py-3 text-xs font-black uppercase tracking-widest transition-all",
                        isActive 
                          ? "bg-ink text-parchment translate-x-1" 
                          : "text-ink/60 hover:text-ink hover:bg-ink/5"
                      )}
                      onClick={() => item.href && navigate(item.href)}
                    >
                      {Icon && <Icon className={cn("mr-3 h-4 w-4", isActive ? "text-terracotta" : "text-ink/40")} />}
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Logout Editorial */}
          <div className="p-4 border-t-2 border-ink">
            <button
              className="w-full flex items-center px-4 py-3 text-xs font-black uppercase tracking-widest text-ink hover:bg-terracotta hover:text-parchment transition-all"
              onClick={handleLogout}
            >
              <LogOut className="mr-3 h-4 w-4" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto ml-72 bg-parchment">
        <div className="container mx-auto p-12 max-w-7xl">
          {children}
        </div>
      </main>
    </div>
  );
} 